import { useTranslation } from "react-i18next";
import { addMonths, startOfMonth } from "date-fns";
import cn from "classnames";
import { formatSumWithCurrency, formatDate } from "@utils/formatValues";
import ProgressBar from '@components/ProgressBar';
import { Text, TextSize, TextType, TextWeight, TextColor } from '@components/Text';
import Box from "@components/Box";
import AmountBlock from "../AmountBlock";
import { Tooltip } from '@components/new/Tooltip';
import InfoIcon from '@assets/icons/info-grey-18.svg';
import StarIcon from "@assets/icons/cashback-star.svg?react";
import styles from "./LevelItem.module.scss";

interface AmountWithCurrency {
  amount: number;
  currency: string;
}

type TLevelData = {
  name: string,
  spentFrom: AmountWithCurrency,
  nextLevelFrom?: AmountWithCurrency,
  cashbackPercent: number,
}

const levelColors: Record<string, string> = {
  base: "#8E9EDD",
  bronze: "#F0A85F",
  silver: "#AFB3C2",
  gold: "#F6B606",
  platinum: "#1E3EBC",
}

const LevelItem = ({ data, currentSpend, currentCashback }: {
  data: TLevelData,
  currentSpend?: AmountWithCurrency,
  currentCashback?: AmountWithCurrency,
}) => {
  const { t } = useTranslation();

  if (!data) return null;

  const paymentDate = startOfMonth(addMonths(Date.now(), 1));
  const currentSpendAmount = currentSpend?.amount ?? 0;
  const spentFromAmount = data.spentFrom?.amount ?? 0;
  const nextLevelFromAmount = data.nextLevelFrom?.amount ?? 0;

  const isCurrentLevel = (spentFromAmount <= currentSpendAmount) && (!nextLevelFromAmount || (nextLevelFromAmount > currentSpendAmount));
  const isInactive = spentFromAmount > currentSpendAmount;
  const colorNames = Object.keys(levelColors);
  const tooltipToRight = 2 < (colorNames.length - colorNames.findIndex(v => v === data.name));

  return (
    <div className={cn(styles.level, isInactive && styles.levelInactive, isCurrentLevel && styles.levelCurrent)}>
      <Box pb={3} flex="row" className={styles.iconRow}>
        <StarIcon className={styles[`level-icon--${data.name}`]} />

        {isCurrentLevel ? (
          <Text
            size={TextSize.XS}
            color={TextColor.DISABLED}
          >
            {t(`uniClub.cashbackTab.paymentDate`, { value: formatDate(paymentDate, "dd.MM") })}
          </Text>
        ) : null}
      </Box>
      <div className={styles.valuesRow}>
        <Box pb={4} flex="column">
          <Box pb={1} flex="column">
            <Text
              size={TextSize.M}
              type={TextType.BLOCK}
              weight={TextWeight.BOLD}
            >
              {t(`uniClub.levels.${data.name}`)}
            </Text>
          </Box>
          <Box>
            <Text
              size={TextSize.M}
              type={TextType.BLOCK}
            >
              {isCurrentLevel ? `${t("uniClub.cashbackTab.cashback")} ${data.cashbackPercent} %` : `${data.cashbackPercent}%`}
            </Text>
          </Box>
        </Box>

        {isCurrentLevel ? (
          <>
            <AmountBlock
              label={t("uniClub.cashbackTab.deliveries")}
              value={currentSpendAmount}
              currency={currentSpend?.currency}
            />
            <AmountBlock
              label={(
                <span className={styles.tooltipContainer}>
                  {t("uniClub.cashbackTab.cashback")}
                  <Tooltip
                    action={<img alt="Info" src={InfoIcon} />}
                    position={tooltipToRight ? "right" : "left"}
                    text={t('uniClub.cashbackTab.tooltip')}
                    whiteSpace="pre-wrap"
                  />
                </span>
              )}
              value={currentCashback?.amount ?? 0}
              currency={currentCashback?.currency}
            />
          </>
        ) : null}
      </div>

      <Box pb={1} className={styles.proggressRow}>
        <ProgressBar
          colors={[
            {
              value: 0,
              main: levelColors[data.name] ?? "blue",
              bg: isInactive ? "#F6F6F6" : "white",
            }
          ]}
          progress={isCurrentLevel
            ? (nextLevelFromAmount
              ? ((currentSpendAmount - spentFromAmount) * 100 / (nextLevelFromAmount - spentFromAmount))
              : 100
            ) : (isInactive ? 0 : 100)
          }
          rounded
        />
      </Box>

      {isCurrentLevel ? (
        <div className={styles.progessbarRowCurrent}>
          <Text size={TextSize.XS} color={TextColor.DISABLED}>
            {formatSumWithCurrency(spentFromAmount ?? 0, data.spentFrom?.currency)}
          </Text>
          <Text size={TextSize.XS} color={TextColor.DISABLED}>
            {nextLevelFromAmount ? formatSumWithCurrency(nextLevelFromAmount ?? 0, data.nextLevelFrom?.currency ?? "USD") : "∞"}
          </Text>
        </div>
      ) : (
        <div className={styles.progessbarRow}>
          <Text size={TextSize.XS} color={TextColor.DISABLED}>
            {t('uniClub.cashbackTab.from', { value: formatSumWithCurrency(spentFromAmount ?? 0, data.spentFrom?.currency) })}
          </Text>
        </div>
      )}
    </div>
  )
}

export default LevelItem;
